// Auto-prompt the browser push permission on the user's first dashboard visit.
//
// Fires at most once per device (PUSH_PROMPT_KEY gate). If the browser allows a
// silent Notification.requestPermission() call (Chrome), the native prompt
// appears immediately. If the result is "default" (user dismissed or browser
// requires a user gesture first), the hook sets showBanner=true so the caller
// can render a dismissible banner whose button triggers the native prompt.
//
// On successful subscription the optional `onAccepted` callback fires exactly
// once — the dashboard uses it to PATCH all push prefs to ON so existing users
// whose stored prefs had push=OFF get updated.
//
// Callback timing is gated by PUSH_ACCEPT_PENDING_KEY (a short-lived
// localStorage flag):
//  - Written immediately before calling push.enable().
//  - Cleared by the push.subscribed effect when subscription succeeds.
//  - Expired by a 30-second timer on failure so later manual subscribes
//    (e.g. from My Profile) never trigger the callback.
// This keeps onAccepted scoped to the first-visit flow without any ref-leak
// risk from enable() failing silently (enable() never throws — it sets an
// error state instead).
import { useCallback, useEffect, useRef, useState } from "react";
import type { PushSubscriptionState } from "./use-push-subscription";

const PUSH_PROMPT_KEY = "padpulse:push-auto-prompt-v1";
const PUSH_ACCEPT_PENDING_KEY = "padpulse:push-first-visit-accept-pending";
const ACCEPT_PENDING_EXPIRY_MS = 30_000;

export type FirstVisitPushPromptState = {
  showBanner: boolean;
  acceptBanner: () => Promise<void>;
  dismissBanner: () => void;
};

export function useFirstVisitPushPrompt(
  push: PushSubscriptionState,
  onAccepted?: () => void | Promise<void>,
): FirstVisitPushPromptState {
  const [showBanner, setShowBanner] = useState(false);

  const attemptedRef = useRef(false);
  const onAcceptedRef = useRef(onAccepted);
  onAcceptedRef.current = onAccepted;

  // Fire onAccepted exactly once: when a subscription appears AND we're within
  // an active first-visit acceptance window (PUSH_ACCEPT_PENDING_KEY is set).
  // The key is set before calling push.enable() and cleared here on success.
  // A 30-second timer clears it on failure so later manual enables (e.g. from
  // My Profile) never trigger the callback — even if enable() failed silently
  // and left the window open.
  useEffect(() => {
    if (!push.subscribed) return;
    if (!localStorage.getItem(PUSH_ACCEPT_PENDING_KEY)) return;
    localStorage.removeItem(PUSH_ACCEPT_PENDING_KEY);
    void onAcceptedRef.current?.();
  }, [push.subscribed]);

  // Open a first-visit acceptance window. Writes the pending key and schedules
  // its expiry. Must be called immediately before push.enable(). The caller
  // must NOT explicitly remove the key — the subscribed effect or the timer
  // will do it. Removing it early would cause the effect (which fires after
  // React's async render) to miss the key and skip onAccepted.
  function openAcceptWindow(): void {
    localStorage.setItem(PUSH_ACCEPT_PENDING_KEY, "1");
    setTimeout(
      () => localStorage.removeItem(PUSH_ACCEPT_PENDING_KEY),
      ACCEPT_PENDING_EXPIRY_MS,
    );
  }

  useEffect(() => {
    if (push.configured === null) return;
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    const skip = (reason: string) => {
      localStorage.setItem(PUSH_PROMPT_KEY, reason);
    };

    if (localStorage.getItem(PUSH_PROMPT_KEY)) return;

    if (!push.supported || push.configured === false) {
      skip("unsupported");
      return;
    }

    if (push.subscribed) {
      skip("already-subscribed");
      return;
    }

    if (push.permission === "denied") {
      skip("denied");
      return;
    }

    localStorage.setItem(PUSH_PROMPT_KEY, "attempted");

    if (push.permission === "granted") {
      // Permission already granted; subscribe immediately.
      openAcceptWindow();
      void push.enable();
      return;
    }

    void (async () => {
      try {
        const perm = await Notification.requestPermission();
        if (perm === "granted") {
          openAcceptWindow();
          await push.enable();
        } else if (perm === "default") {
          // Browser requires a user gesture, or the user dismissed without choosing.
          setShowBanner(true);
        }
        // perm === "denied" → permanently blocked; banner can't help, stay hidden.
      } catch {
        // Some browsers throw instead of resolving when a gesture is required.
        setShowBanner(true);
      }
    })();
  }, [push.configured, push.supported, push.subscribed, push.permission, push.enable]);

  const acceptBanner = useCallback(async () => {
    setShowBanner(false);
    // Open a fresh acceptance window before enabling. If push.enable() fails
    // (no throw — enable catches internally), the 30s timer clears the key so
    // later manual subscribes are unaffected.
    openAcceptWindow();
    await push.enable();
  }, [push.enable]);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  return { showBanner, acceptBanner, dismissBanner };
}
